/**
 * ClinicalPage reducer — typed state + action union for the 82 form-related
 * useState hooks that were tightly coupled. Data collections (notes, surgeries,
 * etc.), page-level UI (loading, error), and per-entity async ops (pdfLoading)
 * stay as independent useState hooks in the component.
 *
 * Zero visual or behavioural change — pure structural refactor.
 */

import type { CodeItem } from '../../components/CodeSelector';
import type {
  BabySex,
  ClinicalNote,
  DeliveryMode,
  Prescription,
  SuggestedPrescription,
  Surgery,
  TranscribeResult,
  UterotonicOption,
  VaginalDelivery,
} from '../../types/clinical';

// ── State ───────────────────────────────────────────────────────────

export interface ClinicalState {
  // SOAP editor
  editing: boolean;
  showRecorder: boolean;
  transcribing: boolean;
  transcribeStep: 'transcribing' | 'structuring' | null;
  soapSaving: boolean;
  subjective: string;
  objective: string;
  assessment: string;
  plan: string;
  summary: string;
  followUp: string;
  bp: string;
  weight: string;
  icd10Suggested: string[];
  icd10Confirmed: string[];
  nhrplCodes: string[];
  suggestedPrescriptions: SuggestedPrescription[];
  hospitalId: string;
  audioBlob: Blob | null;

  // Manual note
  showManualNote: boolean;
  manualTitle: string;
  manualContent: string;
  manualDate: string;
  manualSaving: boolean;

  // Prescription form
  showRxForm: boolean;
  rxDrug: string;
  rxDose: string;
  rxFreq: string;
  rxDuration: string;
  rxQty: number;
  rxRepeats: number;

  // Surgery form
  showSurgeryForm: boolean;
  surgProcedure: string;
  surgHospital: string;
  surgDate: string;
  surgEbl: string;
  surgAnaesthesiaType: string;
  surgAnaesthesiaOther: string;
  surgAnaesthetist: string;
  surgCaseType: '' | 'elective' | 'emergency';
  surgSurgicalAssistant: string;
  surgIndications: string;
  surgProcedureNotes: string;
  surgFindings: string;
  surgComplications: string;
  surgPostOp: string;
  surgIcd10: CodeItem[];

  // Vaginal delivery form
  showDeliveryForm: boolean;
  deliverySaving: boolean;
  vdDate: string;
  vdTime: string;
  vdHospital: string;
  vdDoctorName: string;
  vdDoctorEmail: string;
  vdPaediatrician: string;
  vdWardSister: string;
  vdUmbilicalCordPh: string;
  vdHistory: string;
  vdDeliveryMode: DeliveryMode;
  vdUterotonic: UterotonicOption;
  vdUterotonicOther: string;
  vdNotes: string;
  vdPostPartum: string;
  vdBabyWeight: number | null;
  vdBabySex: BabySex;
  vdApgar1min: number | null;
  vdApgar5min: number | null;
  vdBcgGiven: boolean;
  vdPolioGiven: boolean;

  // Report form
  showReportForm: boolean;
  reportType: string;
  reportFindings: string;
  reportDraft: string;
  reportDrafting: boolean;
  reportSaving: boolean;

  // Obstetric consultation form
  showObsForm: boolean;
  obsFormPregnancyId: string | null;

  // Inline editing
  editingNoteId: string | null;
  editingNoteData: Partial<ClinicalNote>;
  editingSurgId: string | null;
  editingSurgData: Partial<Surgery>;
  editingRxId: string | null;
  editingRxData: Partial<Prescription>;
  editingDeliveryId: string | null;
  editingDeliveryData: Partial<VaginalDelivery>;
  editSaving: boolean;
}

// ── Initial state ───────────────────────────────────────────────────

const today = () => new Date().toISOString().slice(0, 10);

export const initialState: ClinicalState = {
  // SOAP editor
  editing: false,
  showRecorder: false,
  transcribing: false,
  transcribeStep: null,
  soapSaving: false,
  subjective: '',
  objective: '',
  assessment: '',
  plan: '',
  summary: '',
  followUp: '',
  bp: '',
  weight: '',
  icd10Suggested: [],
  icd10Confirmed: [],
  nhrplCodes: [],
  suggestedPrescriptions: [],
  hospitalId: '',
  audioBlob: null,

  // Manual note
  showManualNote: false,
  manualTitle: '',
  manualContent: '',
  manualDate: today(),
  manualSaving: false,

  // Prescription form
  showRxForm: false,
  rxDrug: '',
  rxDose: '',
  rxFreq: '',
  rxDuration: '',
  rxQty: 1,
  rxRepeats: 0,

  // Surgery form
  showSurgeryForm: false,
  surgProcedure: '',
  surgHospital: '',
  surgDate: '',
  surgEbl: '',
  surgAnaesthesiaType: '',
  surgAnaesthesiaOther: '',
  surgAnaesthetist: '',
  surgCaseType: '',
  surgSurgicalAssistant: '',
  surgIndications: '',
  surgProcedureNotes: '',
  surgFindings: '',
  surgComplications: '',
  surgPostOp: '',
  surgIcd10: [],

  // Vaginal delivery form
  showDeliveryForm: false,
  deliverySaving: false,
  vdDate: today(),
  vdTime: '',
  vdHospital: '',
  vdDoctorName: '',
  vdDoctorEmail: '',
  vdPaediatrician: '',
  vdWardSister: '',
  vdUmbilicalCordPh: '',
  vdHistory: '',
  vdDeliveryMode: '',
  vdUterotonic: '',
  vdUterotonicOther: '',
  vdNotes: '',
  vdPostPartum: '',
  vdBabyWeight: null,
  vdBabySex: '',
  vdApgar1min: null,
  vdApgar5min: null,
  vdBcgGiven: false,
  vdPolioGiven: false,

  // Report form
  showReportForm: false,
  reportType: 'referral_letter',
  reportFindings: '',
  reportDraft: '',
  reportDrafting: false,
  reportSaving: false,

  // Obstetric consultation form
  showObsForm: false,
  obsFormPregnancyId: null,

  // Inline editing
  editingNoteId: null,
  editingNoteData: {},
  editingSurgId: null,
  editingSurgData: {},
  editingRxId: null,
  editingRxData: {},
  editingDeliveryId: null,
  editingDeliveryData: {},
  editSaving: false,
};

// ── Actions ─────────────────────────────────────────────────────────

export type ClinicalAction =
  // SOAP editor
  | { type: 'OPEN_SOAP_EDITOR'; showRecorder?: boolean }
  | { type: 'CLOSE_SOAP_EDITOR' }
  | { type: 'SET_SOAP_FIELD'; field: keyof Pick<ClinicalState, 'subjective' | 'objective' | 'assessment' | 'plan' | 'summary' | 'followUp'>; value: string }
  | { type: 'SET_VITAL'; field: keyof Pick<ClinicalState, 'bp' | 'weight'>; value: string }
  | { type: 'LOAD_TRANSCRIPTION'; result: TranscribeResult }
  | { type: 'RESTORE_SOAP_DRAFT'; draft: Record<string, unknown> }
  | { type: 'RESET_SOAP_FORM' }
  | { type: 'SET_TRANSCRIBING'; step: 'transcribing' | 'structuring' | null }
  | { type: 'SET_SOAP_SAVING'; saving: boolean }
  | { type: 'SET_AUDIO_BLOB'; blob: Blob | null }
  | { type: 'SET_HOSPITAL_ID'; id: string }
  | { type: 'SET_ICD10_SUGGESTED'; codes: string[] }
  | { type: 'SET_ICD10_CONFIRMED'; codes: string[] }
  | { type: 'SET_NHRPL_CODES'; codes: string[] }
  | { type: 'SET_SUGGESTED_PRESCRIPTIONS'; prescriptions: SuggestedPrescription[] }
  | { type: 'ACCEPT_PRESCRIPTION'; drugName: string }

  // Manual note
  | { type: 'OPEN_MANUAL_NOTE' }
  | { type: 'CLOSE_MANUAL_NOTE' }
  | { type: 'SET_MANUAL_FIELD'; field: 'manualTitle' | 'manualContent' | 'manualDate'; value: string }
  | { type: 'RESTORE_MANUAL_DRAFT'; draft: Record<string, unknown> }
  | { type: 'SET_MANUAL_SAVING'; saving: boolean }

  // Prescription form
  | { type: 'OPEN_RX_FORM' }
  | { type: 'CLOSE_RX_FORM' }
  | { type: 'SET_RX_FIELD'; field: keyof Pick<ClinicalState, 'rxDrug' | 'rxDose' | 'rxFreq' | 'rxDuration'>; value: string }
  | { type: 'SET_RX_NUMBER_FIELD'; field: keyof Pick<ClinicalState, 'rxQty' | 'rxRepeats'>; value: number }
  | { type: 'RESET_RX_FORM' }

  // Surgery form
  | { type: 'OPEN_SURGERY_FORM' }
  | { type: 'CLOSE_SURGERY_FORM' }
  | { type: 'SET_SURGERY_FIELD'; field: string; value: string }
  | { type: 'SET_SURGERY_CASE_TYPE'; value: '' | 'elective' | 'emergency' }
  | { type: 'SET_SURGERY_ICD10'; codes: CodeItem[] }
  | { type: 'RESET_SURGERY_FORM' }

  // Delivery form
  | { type: 'OPEN_DELIVERY_FORM' }
  | { type: 'CLOSE_DELIVERY_FORM' }
  | { type: 'SET_DELIVERY_FIELD'; field: string; value: string }
  | { type: 'SET_DELIVERY_MODE'; value: DeliveryMode }
  | { type: 'SET_DELIVERY_UTEROTONIC'; value: UterotonicOption }
  | { type: 'SET_DELIVERY_SAVING'; saving: boolean }
  | { type: 'SET_DELIVERY_BABY_WEIGHT'; value: number | null }
  | { type: 'SET_DELIVERY_BABY_SEX'; value: BabySex }
  | { type: 'SET_DELIVERY_APGAR1'; value: number | null }
  | { type: 'SET_DELIVERY_APGAR5'; value: number | null }
  | { type: 'SET_DELIVERY_BCG'; value: boolean }
  | { type: 'SET_DELIVERY_POLIO'; value: boolean }
  | { type: 'RESET_DELIVERY_FORM' }

  // Obstetric consultation form
  | { type: 'OPEN_OBS_FORM'; pregnancyId: string }
  | { type: 'CLOSE_OBS_FORM' }

  // Report form
  | { type: 'OPEN_REPORT_FORM' }
  | { type: 'CLOSE_REPORT_FORM' }
  | { type: 'SET_REPORT_FIELD'; field: keyof Pick<ClinicalState, 'reportType' | 'reportFindings' | 'reportDraft'>; value: string }
  | { type: 'SET_REPORT_DRAFTING'; drafting: boolean }
  | { type: 'SET_REPORT_SAVING'; saving: boolean }

  // Inline editing
  | { type: 'START_EDIT_NOTE'; id: string; data: Partial<ClinicalNote> }
  | { type: 'CANCEL_EDIT_NOTE' }
  | { type: 'UPDATE_EDIT_NOTE_DATA'; data: Partial<ClinicalNote> }
  | { type: 'START_EDIT_SURGERY'; id: string; data: Partial<Surgery> }
  | { type: 'CANCEL_EDIT_SURGERY' }
  | { type: 'UPDATE_EDIT_SURGERY_DATA'; data: Partial<Surgery> }
  | { type: 'START_EDIT_RX'; id: string; data: Partial<Prescription> }
  | { type: 'CANCEL_EDIT_RX' }
  | { type: 'UPDATE_EDIT_RX_DATA'; data: Partial<Prescription> }
  | { type: 'START_EDIT_DELIVERY'; id: string; data: Partial<VaginalDelivery> }
  | { type: 'CANCEL_EDIT_DELIVERY' }
  | { type: 'UPDATE_EDIT_DELIVERY_DATA'; data: Partial<VaginalDelivery> }
  | { type: 'SET_EDIT_SAVING'; saving: boolean };

// ── Reducer ─────────────────────────────────────────────────────────

export function clinicalReducer(state: ClinicalState, action: ClinicalAction): ClinicalState {
  switch (action.type) {
    // ── SOAP editor ──
    case 'OPEN_SOAP_EDITOR':
      return { ...state, editing: true, showRecorder: action.showRecorder ?? false };
    case 'CLOSE_SOAP_EDITOR':
      return { ...state, editing: false, showRecorder: false };
    case 'SET_SOAP_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_VITAL':
      return { ...state, [action.field]: action.value };
    case 'LOAD_TRANSCRIPTION':
      return {
        ...state,
        subjective: action.result.soap.subjective,
        objective: action.result.soap.objective,
        assessment: action.result.soap.assessment,
        plan: action.result.soap.plan,
        summary: action.result.soap.summary || '',
        followUp: action.result.soap.followUp || '',
        icd10Suggested: action.result.soap.icd10Codes || [],
        icd10Confirmed: action.result.soap.icd10Codes || [],
        nhrplCodes: action.result.soap.nhrplCodes || [],
        suggestedPrescriptions: action.result.suggestedPrescriptions || [],
        editing: true,
      };
    case 'RESTORE_SOAP_DRAFT': {
      const d = action.draft;
      return {
        ...state,
        subjective: (d.subjective as string) || '',
        objective: (d.objective as string) || '',
        assessment: (d.assessment as string) || '',
        plan: (d.plan as string) || '',
        summary: (d.summary as string) || '',
        followUp: (d.followUp as string) || '',
        bp: (d.bp as string) || '',
        weight: (d.weight as string) || '',
        icd10Confirmed: (d.icd10Confirmed as string[]) || [],
        nhrplCodes: (d.nhrplCodes as string[]) || [],
        hospitalId: (d.hospitalId as string) || '',
        editing: true,
      };
    }
    case 'RESET_SOAP_FORM':
      return {
        ...state,
        subjective: '',
        objective: '',
        assessment: '',
        plan: '',
        summary: '',
        followUp: '',
        bp: '',
        weight: '',
        icd10Suggested: [],
        icd10Confirmed: [],
        nhrplCodes: [],
        suggestedPrescriptions: [],
        audioBlob: null,
        hospitalId: '',
      };
    case 'SET_TRANSCRIBING':
      return action.step === null
        ? { ...state, transcribing: false, transcribeStep: null }
        : { ...state, transcribing: true, transcribeStep: action.step };
    case 'SET_SOAP_SAVING':
      return { ...state, soapSaving: action.saving };
    case 'SET_AUDIO_BLOB':
      return { ...state, audioBlob: action.blob };
    case 'SET_HOSPITAL_ID':
      return { ...state, hospitalId: action.id };
    case 'SET_ICD10_SUGGESTED':
      return { ...state, icd10Suggested: action.codes };
    case 'SET_ICD10_CONFIRMED':
      return { ...state, icd10Confirmed: action.codes };
    case 'SET_NHRPL_CODES':
      return { ...state, nhrplCodes: action.codes };
    case 'SET_SUGGESTED_PRESCRIPTIONS':
      return { ...state, suggestedPrescriptions: action.prescriptions };
    case 'ACCEPT_PRESCRIPTION':
      return {
        ...state,
        suggestedPrescriptions: state.suggestedPrescriptions.filter(
          p => p.drugName !== action.drugName
        ),
      };

    // ── Manual note ──
    case 'OPEN_MANUAL_NOTE':
      return { ...state, showManualNote: true };
    case 'CLOSE_MANUAL_NOTE':
      return { ...state, showManualNote: false, manualTitle: '', manualContent: '' };
    case 'SET_MANUAL_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESTORE_MANUAL_DRAFT': {
      const d = action.draft;
      return {
        ...state,
        manualTitle: (d.manualTitle as string) || '',
        manualContent: (d.manualContent as string) || '',
        manualDate: (d.manualDate as string) || today(),
        showManualNote: true,
      };
    }
    case 'SET_MANUAL_SAVING':
      return { ...state, manualSaving: action.saving };

    // ── Prescription form ──
    case 'OPEN_RX_FORM':
      return { ...state, showRxForm: true };
    case 'CLOSE_RX_FORM':
      return { ...state, showRxForm: false };
    case 'SET_RX_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_RX_NUMBER_FIELD':
      return { ...state, [action.field]: action.value };
    case 'RESET_RX_FORM':
      return {
        ...state,
        showRxForm: false,
        rxDrug: '',
        rxDose: '',
        rxFreq: '',
        rxDuration: '',
        rxQty: 1,
        rxRepeats: 0,
      };

    // ── Surgery form ──
    case 'OPEN_SURGERY_FORM':
      return { ...state, showSurgeryForm: true };
    case 'CLOSE_SURGERY_FORM':
      return { ...state, showSurgeryForm: false };
    case 'SET_SURGERY_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_SURGERY_CASE_TYPE':
      return { ...state, surgCaseType: action.value };
    case 'SET_SURGERY_ICD10':
      return { ...state, surgIcd10: action.codes };
    case 'RESET_SURGERY_FORM':
      return {
        ...state,
        showSurgeryForm: false,
        surgProcedure: '',
        surgHospital: '',
        surgDate: '',
        surgEbl: '',
        surgAnaesthesiaType: '',
        surgAnaesthesiaOther: '',
        surgAnaesthetist: '',
        surgCaseType: '',
        surgSurgicalAssistant: '',
        surgIndications: '',
        surgProcedureNotes: '',
        surgFindings: '',
        surgComplications: '',
        surgPostOp: '',
        surgIcd10: [],
      };

    // ── Delivery form ──
    case 'OPEN_DELIVERY_FORM':
      return { ...state, showDeliveryForm: true };
    case 'CLOSE_DELIVERY_FORM':
      return { ...state, showDeliveryForm: false };
    case 'SET_DELIVERY_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_DELIVERY_MODE':
      return { ...state, vdDeliveryMode: action.value };
    case 'SET_DELIVERY_UTEROTONIC':
      return { ...state, vdUterotonic: action.value };
    case 'SET_DELIVERY_SAVING':
      return { ...state, deliverySaving: action.saving };
    case 'SET_DELIVERY_BABY_WEIGHT':
      return { ...state, vdBabyWeight: action.value };
    case 'SET_DELIVERY_BABY_SEX':
      return { ...state, vdBabySex: action.value };
    case 'SET_DELIVERY_APGAR1':
      return { ...state, vdApgar1min: action.value };
    case 'SET_DELIVERY_APGAR5':
      return { ...state, vdApgar5min: action.value };
    case 'SET_DELIVERY_BCG':
      return { ...state, vdBcgGiven: action.value };
    case 'SET_DELIVERY_POLIO':
      return { ...state, vdPolioGiven: action.value };
    case 'RESET_DELIVERY_FORM':
      return {
        ...state,
        showDeliveryForm: false,
        deliverySaving: false,
        vdDate: today(),
        vdTime: '',
        vdHospital: '',
        vdDoctorName: '',
        vdDoctorEmail: '',
        vdPaediatrician: '',
        vdWardSister: '',
        vdUmbilicalCordPh: '',
        vdHistory: '',
        vdDeliveryMode: '',
        vdUterotonic: '',
        vdUterotonicOther: '',
        vdNotes: '',
        vdPostPartum: '',
        vdBabyWeight: null,
        vdBabySex: '',
        vdApgar1min: null,
        vdApgar5min: null,
        vdBcgGiven: false,
        vdPolioGiven: false,
      };

    // ── Obstetric consultation form ──
    case 'OPEN_OBS_FORM':
      return { ...state, showObsForm: true, obsFormPregnancyId: action.pregnancyId };
    case 'CLOSE_OBS_FORM':
      return { ...state, showObsForm: false, obsFormPregnancyId: null };

    // ── Report form ──
    case 'OPEN_REPORT_FORM':
      return { ...state, showReportForm: true };
    case 'CLOSE_REPORT_FORM':
      return {
        ...state,
        showReportForm: false,
        reportFindings: '',
        reportDraft: '',
      };
    case 'SET_REPORT_FIELD':
      return { ...state, [action.field]: action.value };
    case 'SET_REPORT_DRAFTING':
      return { ...state, reportDrafting: action.drafting };
    case 'SET_REPORT_SAVING':
      return { ...state, reportSaving: action.saving };

    // ── Inline editing ──
    case 'START_EDIT_NOTE':
      return { ...state, editingNoteId: action.id, editingNoteData: action.data };
    case 'CANCEL_EDIT_NOTE':
      return { ...state, editingNoteId: null, editingNoteData: {} };
    case 'UPDATE_EDIT_NOTE_DATA':
      return { ...state, editingNoteData: action.data };
    case 'START_EDIT_SURGERY':
      return { ...state, editingSurgId: action.id, editingSurgData: action.data };
    case 'CANCEL_EDIT_SURGERY':
      return { ...state, editingSurgId: null, editingSurgData: {} };
    case 'UPDATE_EDIT_SURGERY_DATA':
      return { ...state, editingSurgData: action.data };
    case 'START_EDIT_RX':
      return { ...state, editingRxId: action.id, editingRxData: action.data };
    case 'CANCEL_EDIT_RX':
      return { ...state, editingRxId: null, editingRxData: {} };
    case 'UPDATE_EDIT_RX_DATA':
      return { ...state, editingRxData: action.data };
    case 'START_EDIT_DELIVERY':
      return { ...state, editingDeliveryId: action.id, editingDeliveryData: action.data };
    case 'CANCEL_EDIT_DELIVERY':
      return { ...state, editingDeliveryId: null, editingDeliveryData: {} };
    case 'UPDATE_EDIT_DELIVERY_DATA':
      return { ...state, editingDeliveryData: action.data };
    case 'SET_EDIT_SAVING':
      return { ...state, editSaving: action.saving };

    default:
      return state;
  }
}
